import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

// ============================================
// CONFETTI ANIMATION
// ============================================
export function Confetti({ active, duration = 3000 }) {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (active) {
      const colors = ['#10b981', '#f59e0b', '#3b82f6', '#ec4899', '#8b5cf6', '#06b6d4'];
      const newParticles = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        color: colors[Math.floor(Math.random() * colors.length)],
        delay: Math.random() * 0.5,
        size: Math.random() * 8 + 4,
        rotation: Math.random() * 360,
      }));
      setParticles(newParticles);
      
      const timer = setTimeout(() => setParticles([]), duration);
      return () => clearTimeout(timer);
    }
  }, [active, duration]);

  if (particles.length === 0) return null;

  return createPortal(
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute animate-confetti"
          style={{
            left: `${particle.x}%`,
            top: '-20px',
            width: particle.size,
            height: particle.size,
            backgroundColor: particle.color,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            animationDelay: `${particle.delay}s`,
            transform: `rotate(${particle.rotation}deg)`,
          }}
        />
      ))}
    </div>,
    document.body
  );
}

// ============================================
// XP POINTS ANIMATION
// ============================================
export function XPAnimation({ points, show, onComplete }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show) {
      setVisible(true);
      const timer = setTimeout(() => {
        setVisible(false);
        onComplete?.();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [show, onComplete]);

  if (!visible) return null;

  return createPortal(
    <div className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center">
      <div className="animate-xp-float text-center">
        <div className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-500 drop-shadow-lg">
          +{points} XP
        </div>
        <div className="flex justify-center gap-1 mt-2">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="w-2 h-2 bg-yellow-400 rounded-full animate-sparkle"
              style={{ animationDelay: `${i * 0.1}s` }}
            />
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ============================================
// LEVEL UP MODAL
// ============================================
export function LevelUpModal({ level, show, onClose }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show) {
      setVisible(true);
    }
  }, [show]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => onClose?.(), 300);
  };

  if (!show && !visible) return null;

  return createPortal(
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
      onClick={handleClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      {/* Modal */}
      <div 
        className={`relative bg-gradient-to-br from-purple-600 via-violet-600 to-indigo-700 rounded-3xl p-8 shadow-2xl transform transition-all duration-500 ${visible ? 'scale-100 rotate-0' : 'scale-50 rotate-12'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Glow effect */}
        <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-500 rounded-3xl blur-lg opacity-75 animate-pulse" />
        
        <div className="relative text-center">
          {/* Stars */}
          <div className="flex justify-center gap-2 mb-4">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="text-4xl animate-bounce"
                style={{ animationDelay: `${i * 0.15}s` }}
              >
                ⭐
              </div>
            ))}
          </div>
          
          {/* Level badge */}
          <div className="relative inline-block">
            <div className="w-32 h-32 bg-gradient-to-br from-yellow-300 to-amber-500 rounded-full flex items-center justify-center shadow-xl animate-pulse-slow">
              <div className="text-center">
                <div className="text-sm font-bold text-amber-900">LEVEL</div>
                <div className="text-5xl font-black text-amber-900">{level}</div>
              </div>
            </div>
            {/* Ring effect */}
            <div className="absolute inset-0 rounded-full border-4 border-yellow-300 animate-ping opacity-50" />
          </div>
          
          {/* Text */}
          <h2 className="text-3xl font-black text-white mt-6 mb-2 animate-bounce-slow">
            LEVEL UP! 🎉
          </h2>
          <p className="text-purple-200 mb-6">
            Amazing progress! Keep learning!
          </p>
          
          {/* Button */}
          <button
            onClick={handleClose}
            className="px-8 py-3 bg-white text-purple-700 font-bold rounded-full shadow-lg hover:scale-105 transform transition-all"
          >
            Continue Learning
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ============================================
// STREAK FIRE ANIMATION
// ============================================
export function StreakFire({ streak, showAnimation = false }) {
  const [flames, setFlames] = useState([]);

  useEffect(() => {
    if (showAnimation && streak > 0) {
      const newFlames = Array.from({ length: 8 }, (_, i) => ({
        id: i,
        delay: i * 0.1,
        x: 50 + (Math.random() - 0.5) * 30,
      }));
      setFlames(newFlames);
      
      const timer = setTimeout(() => setFlames([]), 2000);
      return () => clearTimeout(timer);
    }
  }, [showAnimation, streak]);

  return (
    <div className="relative inline-flex items-center gap-1">
      {/* Fire emoji with animation */}
      <span className={`text-2xl ${streak > 0 ? 'animate-fire-flicker' : ''}`}>
        🔥
      </span>
      
      {/* Streak count */}
      <span className="font-bold text-orange-600">{streak}</span>
      
      {/* Floating flames on animation trigger */}
      {flames.map((flame) => (
        <div
          key={flame.id}
          className="absolute bottom-full animate-flame-rise text-xl"
          style={{
            left: `${flame.x}%`,
            animationDelay: `${flame.delay}s`,
          }}
        >
          🔥
        </div>
      ))}
    </div>
  );
}

// ============================================
// ACHIEVEMENT BADGE POP
// ============================================
export function AchievementBadge({ badge, show, onComplete }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show) {
      setVisible(true);
      const timer = setTimeout(() => {
        setVisible(false);
        onComplete?.();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [show, onComplete]);

  if (!visible) return null;

  const badgeConfig = {
    first_question: { icon: '🎯', title: 'First Question!', color: 'from-blue-500 to-cyan-500' },
    streak_3: { icon: '🔥', title: '3 Day Streak!', color: 'from-orange-500 to-red-500' },
    streak_7: { icon: '⚡', title: 'Week Warrior!', color: 'from-yellow-500 to-amber-500' },
    streak_14: { icon: '💫', title: '2 Week Champion!', color: 'from-purple-500 to-pink-500' },
    streak_30: { icon: '👑', title: 'Monthly Master!', color: 'from-amber-500 to-yellow-400' },
    perfect_5: { icon: '⭐', title: 'Perfect 5!', color: 'from-purple-500 to-pink-500' },
    perfect_10: { icon: '🌟', title: 'Perfect 10!', color: 'from-yellow-400 to-orange-500' },
    explorer: { icon: '🧭', title: 'Explorer!', color: 'from-emerald-500 to-teal-500', desc: 'Tried 3 different subjects' },
    quick_learner: { icon: '🚀', title: 'Quick Learner!', color: 'from-indigo-500 to-violet-500', desc: 'Answered 5 questions in under 2 minutes' },
    night_owl: { icon: '🦉', title: 'Night Owl!', color: 'from-indigo-600 to-purple-700', desc: 'Learning after 9 PM' },
    early_bird: { icon: '🐦', title: 'Early Bird!', color: 'from-orange-400 to-yellow-400', desc: 'Learning before 8 AM' },
    bookworm: { icon: '📚', title: 'Bookworm!', color: 'from-emerald-500 to-green-600', desc: 'Completed 50 questions' },
    genius: { icon: '🧠', title: 'Genius!', color: 'from-violet-500 to-purple-600', desc: '90% accuracy over 20 questions' },
    comeback_kid: { icon: '💪', title: 'Comeback Kid!', color: 'from-rose-500 to-red-500', desc: 'Got 3 correct after 3 wrong' },
    subject_master: { icon: '🏆', title: 'Subject Master!', color: 'from-amber-400 to-yellow-500', desc: '100 questions in one subject' },
  };

  const config = badgeConfig[badge] || { icon: '🏆', title: 'Achievement!', color: 'from-gray-500 to-gray-600' };

  return createPortal(
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-badge-pop">
      <div className={`bg-gradient-to-r ${config.color} rounded-2xl px-6 py-4 shadow-2xl flex items-center gap-4`}>
        <div className="text-4xl animate-bounce">{config.icon}</div>
        <div className="text-white">
          <div className="text-xs font-medium opacity-80">Achievement Unlocked!</div>
          <div className="text-lg font-bold">{config.title}</div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ============================================
// PROGRESS RING
// ============================================
export function ProgressRing({ progress, size = 80, strokeWidth = 8, children }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-gray-200"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="url(#progressGradient)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out"
        />
        <defs>
          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#10b981" />
            <stop offset="100%" stopColor="#06b6d4" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        {children || <span className="text-lg font-bold text-gray-700">{Math.round(progress)}%</span>}
      </div>
    </div>
  );
}

// ============================================
// ANIMATED TYPING INDICATOR
// ============================================
export function EnhancedTypingIndicator({ text = "Eduzy is thinking..." }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl rounded-tl-sm border border-gray-200">
      {/* Animated brain icon */}
      <div className="relative">
        <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center animate-pulse">
          <span className="text-white text-sm">🧠</span>
        </div>
        {/* Sparkles around brain */}
        <div className="absolute -top-1 -right-1 w-3 h-3 text-yellow-400 animate-sparkle">✨</div>
      </div>
      
      {/* Text with dots */}
      <div className="flex items-center gap-1">
        <span className="text-gray-600 text-sm font-medium">{text}</span>
        <span className="flex gap-0.5">
          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </span>
      </div>
    </div>
  );
}

// ============================================
// FLOATING ELEMENTS (for Landing Page)
// ============================================
export function FloatingElements() {
  const elements = [
    { emoji: '📚', x: 8, y: 18, delay: 0, duration: 6, color: 'text-amber-200' },
    { emoji: '🎓', x: 88, y: 12, delay: 1, duration: 7, color: 'text-yellow-200' },
    { emoji: '💡', x: 12, y: 65, delay: 2, duration: 5, color: 'text-orange-200' },
    { emoji: '🧮', x: 82, y: 58, delay: 0.5, duration: 6.5, color: 'text-pink-200' },
    { emoji: '📐', x: 4, y: 42, delay: 1.5, duration: 7, color: 'text-purple-200' },
    { emoji: '🔬', x: 92, y: 35, delay: 2.5, duration: 5.5, color: 'text-cyan-200' },
    { emoji: '✏️', x: 22, y: 82, delay: 0.8, duration: 6.2, color: 'text-rose-200' },
    { emoji: '🌟', x: 72, y: 78, delay: 1.8, duration: 5.8, color: 'text-yellow-200' },
    { emoji: '🎯', x: 50, y: 8, delay: 0.3, duration: 6.8, color: 'text-red-200' },
    { emoji: '🏆', x: 35, y: 72, delay: 2.2, duration: 5.2, color: 'text-amber-200' },
  ];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {elements.map((el, i) => (
        <div
          key={i}
          className={`absolute text-4xl opacity-40 animate-float drop-shadow-lg ${el.color}`}
          style={{
            left: `${el.x}%`,
            top: `${el.y}%`,
            animationDelay: `${el.delay}s`,
            animationDuration: `${el.duration}s`,
            filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))',
          }}
        >
          {el.emoji}
        </div>
      ))}
    </div>
  );
}

// ============================================
// CODE SYNTAX HIGHLIGHTER (Simple)
// ============================================
export function CodeBlock({ code, language = 'javascript' }) {
  // Simple syntax highlighting for common patterns
  const highlightCode = (code) => {
    if (!code) return '';
    
    // Keywords
    const keywords = ['const', 'let', 'var', 'function', 'return', 'if', 'else', 'for', 'while', 'class', 'import', 'export', 'from', 'def', 'print', 'True', 'False', 'None'];
    
    let highlighted = code
      // Strings
      .replace(/(["'`])(?:(?!\1)[^\\]|\\.)*\1/g, '<span class="text-emerald-600">$&</span>')
      // Numbers
      .replace(/\b(\d+\.?\d*)\b/g, '<span class="text-amber-600">$1</span>')
      // Comments
      .replace(/(\/\/.*$|#.*$)/gm, '<span class="text-gray-400 italic">$1</span>');
    
    // Keywords
    keywords.forEach(kw => {
      const regex = new RegExp(`\\b(${kw})\\b`, 'g');
      highlighted = highlighted.replace(regex, '<span class="text-purple-600 font-semibold">$1</span>');
    });
    
    return highlighted;
  };

  return (
    <div className="relative group">
      <pre className="bg-gray-900 text-gray-100 rounded-xl p-4 overflow-x-auto text-sm font-mono">
        <code 
          dangerouslySetInnerHTML={{ __html: highlightCode(code) }}
        />
      </pre>
      <div className="absolute top-2 right-2 text-xs text-gray-500 bg-gray-800 px-2 py-1 rounded">
        {language}
      </div>
    </div>
  );
}

// ============================================
// ANIMATED FUN FACT CARD
// ============================================
export function AnimatedFunFact({ fact, icon = '💡', renderMath = null }) {
  // If renderMath function is provided, use it; otherwise render plain text
  const renderedFact = renderMath ? renderMath(fact) : fact;
  
  return (
    <div className="relative overflow-hidden bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 border border-amber-200 rounded-xl p-4 animate-fun-fact-enter">
      {/* Animated background sparkles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-amber-400 rounded-full animate-twinkle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </div>
      
      <div className="relative flex items-start gap-3">
        {/* Animated icon */}
        <div className="w-10 h-10 bg-gradient-to-br from-amber-100 to-yellow-200 rounded-full flex items-center justify-center flex-shrink-0 animate-bounce-slow shadow-md">
          <span className="text-xl">{icon}</span>
        </div>
        
        <div>
          <p className="font-semibold text-amber-800 text-sm mb-1 flex items-center gap-2">
            Did You Know?
            <span className="animate-sparkle">✨</span>
          </p>
          <p className="text-amber-900 text-sm leading-relaxed">{renderedFact}</p>
        </div>
      </div>
    </div>
  );
}
